import { useState, useEffect, useContext } from "react";
import { FilterMatchMode } from "primereact/api";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "../schools/SchoolsTable.css";
import { empty, floatval, isArray } from "../../../../Utilities/utils";
import { NavLink, useNavigate } from "react-router-dom";
import colors from "../../../../config/colors";
import { Checkbox } from "primereact/checkbox";
import { FaCheck } from "react-icons/fa";
import { AuthContext } from "../../../../screens/Root/ProtectedRoute";

const filterValues = [
  { id: 1, value: "Active" },
  { id: 2, value: "In-active" },
];

// single source of truth for "has this student paid"
const hasStudentPaid = (rowData) => rowData?.paid?.toLowerCase() === "yes";

export default function StudentsTable({
  students = [],
  confirmDisableSelected,
  goToUpdateStudentScreen,
  getStudentReportScreen,
  onPageChange,
  rows = 50,
  totalRecords,
  first,
  search = "",
  onSearchChange,
  assessment = false,
  openAssessmentModal,
  openMultipleAssessmentModal,
  recordScores = false,
  reportSheet = false,
  isClassList = false,
  isPromotionList = false,
  goToActivateStudent,
  selectedStudentIds = [],
  setShowAddButton,
  setSelectedStudentIds,
  setChecked,
  updateSelectedStudentIds,
  studentIds,
  promoteStudent,
  activateEmpty,
  graduationList = false,
  academicData = { termId: "", sessionId: "", isStream: false },
  onKeyDown,
  filterValue,
  filterSearch,
  showFilter = true,
  onSort,
  sortField,
  sortOrder,
  usingStaticForm = false
}) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [activeIndex, setActiveIndex] = useState(filterValue);
    const { user } = useContext(AuthContext);
  const { sessionId, termId } = academicData;

  // keep activeIndex in sync if the parent changes filterValue externally
  useEffect(() => {
    setActiveIndex(filterValue);
  }, [filterValue]);

  const derivedTableId =
    [
      assessment && "assessment",
      isClassList && "class-list",
      isPromotionList && "promotion-list",
      graduationList && "graduation-list",
      reportSheet && "report-sheet",
    ]
      .filter(Boolean)
      .join("-") ||
    "default";

  const getSeverity = (status) => (status === true ? "success" : "danger");

  const statusBodyTemplate = (rowData) => {
    const isActive = rowData?.active === true;
    return (
      <Tag
        value={isActive ? "ACTIVE" : "IN-ACTIVE"}
        severity={getSeverity(isActive)}
      />
    );
  };

  const goToProfile = (rowData) => {
    const studentId = rowData?._id || "";
    navigate(`/student/${studentId}/profile`);
  };

  const renderHeader = () => {
    return (
      <div style={{ width: "100%" }}>
        <InputText
          value={search}
          onChange={onSearchChange}
          onKeyDown={onKeyDown}
          placeholder="Student Search"
          className="table-search"
        />
        {(!assessment && !usingStaticForm) && (
          <div className="flex row-g-12 col-g-12 flex-wrap mt-20">
            {showFilter &&
              filterValues.map((data, index) => (
                <div
                  key={data.id}
                  className="subject-assessment-box"
                  style={{
                    color:
                      activeIndex === index + 1 ? colors.white : colors.black,
                    backgroundColor:
                      activeIndex === index + 1 ? colors.primary : colors.white,
                  }}
                  onClick={() => {
                    filterSearch(data.value);
                    setActiveIndex(index + 1);
                  }}
                >
                  {data.value}
                </div>
              ))}
          </div>
        )}
      </div>
    );
  };

  const actionBodyTemplate = (rowData) => {
    const hasPaid = hasStudentPaid(rowData);
    return (
      <div style={{ display: "flex", gap: 8 }}>
        { assessment ? (
          <>
            { recordScores && (
              <>
                <Button
                  icon="pi pi-cloud-upload"
                  style={{
                    width: 35,
                    height: 35,
                    backgroundColor: "transparent",
                    color: colors.success,
                    borderColor: colors.success,
                    borderWidth: 1,
                    borderRadius: "50%",
                  }}
                  onClick={() => openAssessmentModal(rowData, true)}
                />
                <Button
                  icon="pi pi-arrow-circle-up"
                  style={{
                    width: 35,
                    height: 35,
                    backgroundColor: "transparent",
                    color: colors.primary,
                    borderColor: colors.primary,
                    borderWidth: 1,
                    borderRadius: "50%",
                  }}
                  onClick={() => openMultipleAssessmentModal(rowData, true)}
                />
              </>
            ) }
            {reportSheet && (
              <Button
                icon="pi pi-id-card"
                style={{
                  width: 35,
                  height: 35,
                  backgroundColor: "transparent",
                  color: colors.primary,
                  borderColor: colors.primary,
                  borderWidth: 1,
                  borderRadius: "50%",
                }}
                onClick={() => getStudentReportScreen(rowData)}
              />
            )}
          </>
        ) : hasPaid && !isPromotionList ? (
          <>
            <Button
              icon="pi pi-eye"
              style={{
                width: 35,
                height: 35,
                backgroundColor: "transparent",
                color: colors.success,
                borderColor: colors.success,
                borderWidth: 1,
                borderRadius: "50%",
              }}
              onClick={() => goToProfile(rowData)}
            />
            <Button
              icon="pi pi-pencil"
              style={{
                width: 35,
                height: 35,
                backgroundColor: "transparent",
                color: colors.primary,
                borderColor: colors.primary,
                borderWidth: 1,
                borderRadius: "50%",
              }}
              onClick={() => goToUpdateStudentScreen(rowData)}
            />
            <Button
              icon={rowData?.active === true ? "pi pi-times" : "pi pi-check"}
              style={{
                backgroundColor: "transparent",
                width: 35,
                height: 35,
                color: rowData?.active === true ? colors.danger : colors.success,
                borderColor: rowData?.active === true ? colors.danger : colors.success,
                borderWidth: 1,
                borderRadius: "50%",
              }}
              onClick={() => confirmDisableSelected(rowData)}
            />
          </>
        ) : isPromotionList ? (
          <Button
            icon="pi pi-forward"
            style={{
              width: 35,
              height: 35,
              backgroundColor: "transparent",
              color: colors.primary,
              borderColor: colors.primary,
              borderWidth: 1,
              borderRadius: "50%",
            }}
            onClick={() => promoteStudent(rowData)}
          />
        ) : (
          <>
            <Button
              icon="pi pi-wallet"
              style={{
                width: 35,
                height: 35,
                backgroundColor: "transparent",
                color: colors.danger,
                borderColor: colors.danger,
                borderWidth: 1,
                borderRadius: "50%",
              }}
              onClick={() => goToActivateStudent(rowData)}
            />
            <Button
              icon={rowData?.active === true ? "pi pi-times" : "pi pi-check"}
              style={{
                backgroundColor: "transparent",
                width: 35,
                height: 35,
                color: rowData?.active === true ? "#e65061" : "#389d17",
                borderColor: rowData?.active === true ? "#e65061" : "#389d17",
                borderWidth: 1,
                borderRadius: "50%",
              }}
              onClick={() => confirmDisableSelected(rowData)}
            />
          </>
        )}
      </div>
    );
  };

  const checkBoxBodyTemplate = (rowData) => {
    const studentId = rowData?._id || "";
    const hasPaid = hasStudentPaid(rowData);
    if (hasPaid && !isPromotionList) {
      return <FaCheck color={colors.success} />;
    }
    return (
      <Checkbox
        checked={selectedStudentIds.includes(studentId)}
        onChange={() => updateSelectedStudentIds(studentId)}
      />
    );
  };

  const regNoBodyTemplate = (rowData) => {
    const regNo = rowData?.regNo || "";
    const studentId = rowData?._id || "";
    return (
      <NavLink
        to={`/student/${studentId}/profile`}
        style={{ color: colors.primary, textDecoration: "none" }}
      >
        {regNo}
      </NavLink>
    );
  };

  const recordedAssessmentTemplate = (rowData) => {
    const rowAssessments = isArray(rowData?.assessment) ? rowData.assessment : [];

    const currentAssessments = rowAssessments.filter(
      (a) => {
        return  a?.sessionId === sessionId && a?.termId === termId;
      }
    );
    const noOfCAs = user?.noOfCAs || 2;

    const recordedCount = currentAssessments.reduce(
      (count, data) =>
        {
          let isRecorded = true;
          Array.from({ length: noOfCAs || 2 }).forEach((_, idx) => 
            {
              if (!(!empty(data[`ca${idx + 1}`]) || floatval(data[`ca${idx + 1}`], 0) === 0)) {
                isRecorded = false;
              }
            }
          )
          return isRecorded && !empty(data?.exam) && data?.sessionId === sessionId && data?.termId === termId
          ? count + 1
          : count
        },
      0
    );

    return <span>{recordedCount}</span>;
  };

  const header = renderHeader();
  const renderCheckbox = (isClassList && !activateEmpty) || isPromotionList;

  return (
    <div className="datatable">
      <DataTable
        value={students}
        paginator={usingStaticForm ? false: true }
        rows={usingStaticForm ? students?.length : rows }
        header={usingStaticForm ? null : header}
        filters={filters}
        onFilter={(e) => setFilters(e.filters)}
        selection={selectedStudent}
        onSelectionChange={(e) => setSelectedStudent(e.value)}
        selectionMode="single"
        dataKey="_id"
        stateStorage="session"
        stateKey={`dt-state-${derivedTableId}`}
        emptyMessage="No students found."
        tableStyle={{ minWidth: "100%" }}
        onPage={onPageChange}
        lazy={true}
        totalRecords={totalRecords}
        first={first}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={onSort}
      >
        {renderCheckbox && !graduationList ? (
          <Column
            field="_id"
            header={
              <Checkbox
                checked={
                  studentIds.length > 0 &&
                  studentIds.every((id) => selectedStudentIds.includes(id))
                }
                indeterminate={
                  studentIds.length > 0 &&
                  selectedStudentIds.some((id) => studentIds.includes(id)) &&
                  !studentIds.every((id) => selectedStudentIds.includes(id))
                    ? true
                    : undefined
                }
                onChange={(e) => {
                  const isChecked = e.checked;
                  setSelectedStudentIds((prev) => {
                    let newSelection;
                    if (isChecked) {
                      const idsToAdd = studentIds.filter((id) => !prev.includes(id));
                      newSelection = [...prev, ...idsToAdd];
                    } else {
                      newSelection = prev.filter((id) => !studentIds.includes(id));
                    }
                    setShowAddButton(newSelection.length > 0);
                    setChecked(newSelection.length === totalRecords);
                    return newSelection;
                  });
                }}
              />
            }
            style={{ width: "5%" }}
            body={checkBoxBodyTemplate}
          />
        ) : null}
        <Column
          field="regNo"
          header="Reg. No."
          sortable
          filterPlaceholder="Search"
          style={{ width: "10%" }}
          body={regNoBodyTemplate}
        />
        <Column
          field="lastName"
          header="Last Name"
          sortable
          filterPlaceholder="Search"
          style={{ width: "15%" }}
        />
        <Column
          field="firstName"
          header="First Name"
          sortable
          filterPlaceholder="Search"
          style={{ width: "15%" }}
        />
        <Column
          field="middleName"
          header="Middle Name"
          sortable
          filterPlaceholder="Middle name"
          style={{ width: "15%" }}
        />
        {assessment ? (
          <Column
            field="no_of_recorded_assessment"
            header="#Recorded Assessment"
            sortable
            filterPlaceholder="Recorded Assessment"
            style={{ width: "15%" }}
            body={recordedAssessmentTemplate}
          />
        ) : null}
        {(!isClassList && !graduationList && !usingStaticForm) ? (
          <Column
            field="class"
            header="Class"
            sortable
            filterPlaceholder="Class"
            style={{ width: "15%" }}
            body={(rowData) => rowData?.class || "N/A"}
          />
        ) : null}
        {!graduationList && !usingStaticForm && (
          <Column
            field="status"
            header="Status"
            body={statusBodyTemplate}
            sortable
            filterMenuStyle={{ width: "14rem" }}
            style={{ width: "10%" }}
          />
        )}
        {(!graduationList && !usingStaticForm) && (
          <Column
            header="Action"
            body={actionBodyTemplate}
            exportable={false}
            style={{ width: "15%" }}
          />
        )}
      </DataTable>
    </div>
  );
}